import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageSquare, Phone } from "lucide-react";
import { getSmsHref, handleSmsClick } from "@/lib/contact";

const HERO_IMG = "/images/car-hero.webp";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  return (
    <section
      id="start"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Srebrny SUV — samochód firmy TRANSPORT OSÓB"
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-10 pt-24 sm:pb-16 md:px-8 md:pb-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="hero-slogan"
          className="mb-4 font-display text-xs font-semibold tracking-[0.3em] text-[#E2C792] md:mb-6 md:text-sm"
        >
          KOMFORT • BEZPIECZEŃSTWO • PUNKTUALNOŚĆ
        </motion.p>

        <h1 className="font-display text-[15vw] font-extrabold leading-[0.95] tracking-tighter text-white sm:text-7xl md:text-8xl lg:text-9xl">
          <RevealLine delay={0.25}>TRANSPORT</RevealLine>
          <RevealLine delay={0.4}>
            <span className="text-[#E2C792]">OSÓB</span>
          </RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
          className="mt-4 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base md:mt-6 md:text-lg"
        >
          Przejazdy lokalne i krajowe — wesela, lotniska, wyjazdy służbowe,
          przewozy pracowników, dojazdy do lekarza i sanatoriów. Zadzwoń lub
          napisz SMS.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-6 flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:gap-4 md:mt-10"
        >
          <a
            href="tel:+48737473050"
            data-testid="hero-call-button"
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#E2C792] px-9 py-4 font-display text-base font-bold tracking-wide text-[#0A0A0A] transition-[background-color,transform] duration-200 hover:bg-[#D1B273] active:scale-95 sm:w-auto sm:py-5 md:text-lg"
          >
            <Phone className="h-5 w-5" strokeWidth={2.5} />
            ZADZWOŃ — 737 473 050
          </a>
          <a
            href={getSmsHref()}
            onClick={handleSmsClick}
            data-testid="hero-sms-button"
            className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-white/25 bg-white/5 px-9 py-4 font-display text-base font-bold tracking-wide text-white backdrop-blur-md transition-[background-color,border-color,transform] duration-200 hover:border-white/50 hover:bg-white/10 active:scale-95 sm:w-auto sm:py-5 md:text-lg"
          >
            <MessageSquare className="h-5 w-5" strokeWidth={2.5} />
            NAPISZ SMS
          </a>
        </motion.div>
      </div>
    </section>
  );
};
